import { z } from 'zod';
import { ItemAcquisitionPlanSchema } from '@/IsekaiRPG/modules/item/itemTypes';
// ============================================
// 语义任务最小壳
// ============================================

export const SemanticQuestStatusSchema = z.enum(['accepted', 'completed', 'failed', 'reward_claimed']);
export type SemanticQuestStatus = z.infer<typeof SemanticQuestStatusSchema>;

export const SemanticQuestSiteGenerationStatusSchema = z.enum(['not_needed', 'idle', 'running', 'ready', 'failed']);
export type SemanticQuestSiteGenerationStatus = z.infer<typeof SemanticQuestSiteGenerationStatusSchema>;

export const SemanticQuestRewardSnapshotSchema = z.object({
  gold: z.number().int().min(0).default(0),
  items: z.array(ItemAcquisitionPlanSchema).default([]),
  worldEffects: z.record(z.string(), z.union([z.boolean(), z.number(), z.string()])).default({}),
  note: z.string().default(''),
});
export type SemanticQuestRewardSnapshot = z.infer<typeof SemanticQuestRewardSnapshotSchema>;

export const SemanticQuestSiteDirectiveSchema = z.object({
  needed: z.boolean().default(false),
  reason: z.string().default(''),
  hint: z.string().default(''),
});
export type SemanticQuestSiteDirective = z.infer<typeof SemanticQuestSiteDirectiveSchema>;

export const SemanticQuestRecordSchema = z.object({
  questId: z.string().min(1),
  status: SemanticQuestStatusSchema.default('accepted'),
  title: z.string().min(1),
  description: z.string().min(1),
  issuerText: z.string().min(1),
  rewardSnapshot: SemanticQuestRewardSnapshotSchema.default(() => ({
    gold: 0,
    items: [],
    worldEffects: {},
    note: '',
  })),
  siteDirective: SemanticQuestSiteDirectiveSchema.optional(),
  suggestedNextStep: z.string().optional(),
  createdAt: z.number().default(() => Date.now()),
  acceptedAt: z.number().optional(),
  completedAt: z.number().optional(),
  failedAt: z.number().optional(),
  claimedAt: z.number().optional(),
  sourceConversationId: z.string().optional(),
  sourceMessageId: z.string().optional(),
  lastStatusReason: z.string().optional(),
  generatedSiteId: z.string().optional(),
});
export type SemanticQuestRecord = z.infer<typeof SemanticQuestRecordSchema>;

export const SemanticQuestOfferPayloadSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  issuerText: z.string().min(1),
  rewardSnapshot: SemanticQuestRewardSnapshotSchema.default(() => ({
    gold: 0,
    items: [],
    worldEffects: {},
    note: '',
  })),
  siteDirective: SemanticQuestSiteDirectiveSchema.optional(),
  suggestedNextStep: z.string().optional(),
});
export type SemanticQuestOfferPayload = z.infer<typeof SemanticQuestOfferPayloadSchema>;

export const SemanticQuestOfferFlowerOutputSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('none'),
    reason: z.string().default(''),
  }),
  z.object({
    action: z.literal('create_quest'),
    quest: SemanticQuestOfferPayloadSchema,
  }),
]);
export type SemanticQuestOfferFlowerOutput = z.infer<typeof SemanticQuestOfferFlowerOutputSchema>;

export const SemanticQuestJudgeFlowerOutputSchema = z.object({
  result: z.enum(['keep_active', 'complete', 'fail']),
  reason: z.string().default(''),
});
export type SemanticQuestJudgeFlowerOutput = z.infer<typeof SemanticQuestJudgeFlowerOutputSchema>;

// ============================================
// 任务状态
// ============================================

export const QuestsStateSchema = z.object({
  semanticRecords: z.record(z.string(), SemanticQuestRecordSchema).default({}),
  semanticOrder: z.array(z.string()).default([]),
});
export type QuestsState = z.infer<typeof QuestsStateSchema>;
